import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { markSlideViewed, getActivityProgress } from '../utils/progressTracker';

const ACTIVITY_ID = 'bac-assp-bloc2';
const theme = { primary: '#0D9488', secondary: '#0EA5E9', bg: '#F0FDFA', text: '#1a2e35' };
const titleImage = "https://static.prod-images.emergentagent.com/jobs/9345dd8f-d626-4299-a665-42cc7822d693/images/eac0689c8960eaf891fe8671639e444050cd76186c706b1478ea277c1616e3d2.png";
const bgImage = "https://static.prod-images.emergentagent.com/jobs/9345dd8f-d626-4299-a665-42cc7822d693/images/1b86bb9c58ef07933a0550b45dc1c02e555bcaeb2e33b283aa07c8452fd5ab91.png";

const slides = [
  // ===== TITRE =====
  { type: 'title', image: titleImage },

  // ===== SOMMAIRE =====
  { type: 'sommaire', sections: [
    { num: "1", title: "Les soins d'hygiène et de confort", target: 6 },
    { num: "2", title: "Prévention des risques et sécurité", target: 39 },
    { num: "3", title: "Surveillance de l'état de santé", target: 51 },
    { num: "4", title: "Hygiène de l'environnement", target: 71 },
    { num: "5", title: "Alimentation et hydratation", target: 84 },
    { num: "6", title: "Accompagnement à l'élimination", target: 107 },
    { num: "7", title: "Mobilisation, transferts et déplacements", target: 124 },
    { num: "8", title: "Attendus professionnels et VAE", target: 140 },
  ]},

  // ===== INTRODUCTION =====
  { type: 'subsection', title: "Introduction" },
  { type: 'richtext', title: "Objectif du Bloc 2", blocks: [
    { text: "Le Bloc 2 porte sur la réalisation des soins d'hygiène, de confort et de sécurité. Ces soins participent directement au bien-être, à la prévention des complications, au maintien de la santé et à la dignité de la personne." },
  ]},
  { type: 'richtext', title: "Une démarche globale", blocks: [
    { text: "Le professionnel doit maîtriser les gestes techniques, mais aussi comprendre leur finalité, identifier les risques, observer l'état de santé et adapter ses interventions. Les soins ne sont jamais mécaniques : ils s'inscrivent dans une démarche globale, relationnelle et préventive." },
  ]},

  // =============================================
  // CHAPITRE 1 : Les soins d'hygiène et de confort
  // =============================================
  { type: 'section', section: "1", title: "Les soins d'hygiène et de confort" },

  // --- Principes généraux ---
  { type: 'subsection', title: "Principes généraux des soins d'hygiène" },
  { type: 'richtext', title: "Les soins d'hygiène", blocks: [
    { text: "Les soins d'hygiène ne se limitent pas à une action technique. Ils participent au bien-être, à la dignité et à la prévention des complications." },
    { text: "Chaque soin doit respecter plusieurs principes fondamentaux :" },
  ]},
  { type: 'richtext', title: "Respect de la personne", blocks: [
    { text: "Le respect de la personne est essentiel. Cela implique de préserver son intimité, de demander son consentement et d'expliquer chaque geste." },
  ]},
  { type: 'richtext', title: "Sécurité", blocks: [
    { text: "La sécurité doit être assurée à tout moment, notamment en évitant les chutes, les brûlures ou les douleurs." },
  ]},
  { type: 'richtext', title: "Autonomie", blocks: [
    { text: "L'autonomie doit être favorisée. Le professionnel ne doit pas faire à la place, mais accompagner la personne selon ses capacités." },
  ]},
  { type: 'richtext', title: "Observation", blocks: [
    { text: "L'observation constitue un élément clé du soin. Chaque toilette est un moment privilégié pour repérer des anomalies comme des rougeurs, des plaies ou une modification de l'état général." },
  ]},
  { type: 'richtext', title: "Hygiène professionnelle", blocks: [
    { text: "L'hygiène professionnelle est indispensable. Elle repose notamment sur l'hygiène des mains et le respect des protocoles." },
  ]},

  // --- 1.1 Finalités des soins ---
  { type: 'subsection', title: "1.1 Finalités des soins" },
  { type: 'richtext', title: "Finalités des soins", blocks: [
    { text: "Les soins d'hygiène et de confort ont pour objectif de maintenir la propreté corporelle, de prévenir les infections, d'assurer le confort physique et psychologique, de valoriser l'image de soi et de permettre l'observation de l'état de santé. Ils sont aussi un moment important de relation et d'accompagnement." },
  ]},

  // --- 1.2 Principes professionnels et éthiques ---
  { type: 'subsection', title: "1.2 Principes professionnels et éthiques" },
  { type: 'richtext', title: "Principes professionnels et éthiques", blocks: [
    { text: "Chaque soin doit respecter plusieurs principes fondamentaux : le respect de la personne, le respect de l'intimité, la recherche du consentement, l'explication des gestes, la sécurité, le confort, l'ergonomie et la stimulation de l'autonomie. Le professionnel ne doit pas faire à la place lorsque la personne peut participer, même partiellement." },
  ]},

  // --- 1.3 Hygiène des mains ---
  { type: 'subsection', title: "1.3 Hygiène des mains et prévention des infections" },
  { type: 'richtext', title: "Hygiène des mains", blocks: [
    { text: "L'hygiène des mains constitue le premier geste de prévention. Elle doit être réalisée avant et après chaque soin, après tout contact avec la personne ou son environnement et chaque fois que cela est nécessaire. La friction hydroalcoolique est privilégiée lorsque les mains ne sont pas visiblement souillées. Le respect des protocoles limite le risque de transmission des micro-organismes." },
  ]},

  // --- 1.4 Soins d'hygiène corporelle ---
  { type: 'subsection', title: "1.4 Soins d'hygiène corporelle" },
  { type: 'richtext', title: "Les types de soins d'hygiène corporelle", blocks: [
    { text: "Les soins d'hygiène corporelle regroupent notamment :", list: [
      "La toilette au lit",
      "La toilette au lavabo",
      "La douche",
      "Le bain",
    ]},
  ]},

  // Protocole — Toilette au lit
  { type: 'subsection', title: "Protocole — Toilette au lit" },
  { type: 'richtext', title: "Indications", blocks: [
    { text: "La toilette au lit concerne les personnes dépendantes, alitées ou présentant une mobilité réduite." },
  ]},
  { type: 'richtext', title: "Préparation", blocks: [
    { text: "Avant de commencer le soin, le professionnel prépare l'ensemble du matériel :", list: [
      "Gants de toilette",
      "Savon ou solution lavante",
      "Serviettes",
      "Linge propre",
      "Protection",
      "Sac à linge sale",
    ]},
    { text: "La préparation permet d'éviter les interruptions et garantit la qualité du soin. Le professionnel réalise ensuite une hygiène des mains et s'équipe si nécessaire (gants)." },
  ]},
  { type: 'richtext', title: "Installation de la personne", blocks: [
    { list: [
      "La personne est informée du soin afin de favoriser sa participation et de la rassurer.",
      "L'environnement est sécurisé (freins du lit, hauteur adaptée).",
      "La pudeur est respectée grâce à l'utilisation d'un drap ou d'une serviette.",
    ]},
  ]},
  { type: 'richtext', title: "Réalisation du soin", blocks: [
    { text: "La toilette se réalise selon un ordre logique, en allant du plus propre au plus sale.", list: [
      "Le visage est lavé en premier, sans savon si nécessaire, en respectant les yeux.",
      "Le haut du corps est ensuite nettoyé, en insistant sur les plis cutanés.",
      "Les membres supérieurs puis inférieurs sont lavés en favorisant la participation de la personne.",
      "Le dos est réalisé en mobilisant la personne avec précaution.",
      "La toilette intime est réalisée en dernier, avec des gestes adaptés et respectueux.",
    ]},
    { note: "Chez la femme, le nettoyage se fait d'avant en arrière pour éviter les infections. Chez l'homme, une attention particulière est portée au gland. Chaque zone est soigneusement rincée et séchée, notamment les plis cutanés afin d'éviter la macération." },
  ]},
  { type: 'richtext', title: "Observation pendant le soin", blocks: [
    { text: "Pendant toute la toilette, le professionnel observe :", list: [
      "L'état de la peau",
      "Les rougeurs",
      "Les escarres",
      "La douleur",
    ]},
    { text: "Ces observations permettent d'adapter le soin et d'alerter si nécessaire." },
  ]},
  { type: 'richtext', title: "Réinstallation", blocks: [
    { list: [
      "Une fois la toilette terminée, la personne est réinstallée confortablement.",
      "Le lit est remis en ordre et l'environnement est sécurisé.",
      "Le professionnel vérifie que la personne dispose de tout ce dont elle a besoin (sonnette, eau).",
    ]},
  ]},
  { type: 'richtext', title: "Transmission", blocks: [
    { text: "Les informations importantes sont transmises à l'équipe :", list: [
      "Anomalies observées",
      "Comportement de la personne",
      "Participation",
    ]},
  ]},

  // Protocole — Toilette au lavabo
  { type: 'subsection', title: "Protocole — Toilette au lavabo" },
  { type: 'richtext', title: "Toilette au lavabo", blocks: [
    { list: [
      "La toilette au lavabo concerne les personnes partiellement autonomes.",
      "Le professionnel accompagne la personne en sécurisant l'environnement.",
      "Il vérifie la température de l'eau, prépare le matériel et encourage la personne à réaliser elle-même les gestes.",
      "Le rôle du professionnel est de guider, d'aider si nécessaire et de surveiller.",
      "Cette toilette favorise l'autonomie et l'estime de soi.",
    ]},
  ]},

  // Protocole — Douche
  { type: 'subsection', title: "Protocole — Douche" },
  { type: 'richtext', title: "Douche", blocks: [
    { text: "La douche permet d'assurer l'hygiène tout en favorisant le bien-être.", list: [
      "Le matériel est préparé en amont.",
      "La salle de bain est sécurisée (tapis antidérapant, barres d'appui).",
      "La personne est accompagnée dans la douche en respectant son rythme.",
      "Le professionnel vérifie la température de l'eau et adapte son intervention.",
    ]},
    { heading: "Points de vigilance", list: [
      "Risque de chute",
      "Fatigue",
      "Malaise",
    ]},
  ]},

  // Protocole — Bain
  { type: 'subsection', title: "Protocole — Bain" },
  { type: 'richtext', title: "Bain", blocks: [
    { list: [
      "Le bain est utilisé pour le confort et la détente.",
      "Il nécessite une surveillance constante.",
      "Le professionnel ne doit jamais laisser la personne seule.",
      "La température de l'eau doit être adaptée.",
    ]},
  ]},

  // --- 1.5 Soins d'hygiène complémentaires ---
  { type: 'subsection', title: "1.5 Soins d'hygiène complémentaires" },
  { type: 'richtext', title: "Les soins complémentaires", blocks: [
    { text: "Ces soins participent au bien-être global de la personne.", list: [
      "L'hygiène bucco-dentaire permet de prévenir les infections et d'améliorer le confort.",
      "Le soin des cheveux contribue à l'image de soi.",
      "Le soin des ongles doit être réalisé avec prudence, notamment chez les personnes diabétiques.",
      "L'hydratation de la peau permet de prévenir la sécheresse et les lésions.",
    ]},
  ]},
  { type: 'richtext', title: "Dimension professionnelle du soin", blocks: [
    { text: "Un soin d'hygiène ne doit jamais être réalisé de manière mécanique. Le professionnel doit constamment :", list: [
      "Expliquer ses gestes",
      "Adapter son accompagnement",
      "Observer",
      "Prévenir les risques",
    ]},
  ]},

  // =============================================
  // CHAPITRE 2 : Prévention des risques et sécurité
  // =============================================
  { type: 'section', section: "2", title: "Prévention des risques et sécurité" },

  // --- 2.1 Prévention des escarres ---
  { type: 'subsection', title: "2.1 Prévention des escarres" },
  { type: 'richtext', title: "Prévention des escarres", blocks: [
    { text: "Les escarres sont des lésions de la peau liées à une pression prolongée, en particulier au niveau des points d'appui." },
    { text: "Leur prévention repose sur :", list: [
      "Des changements de position réguliers",
      "L'utilisation de matériel adapté",
      "La surveillance de la peau",
      "Une bonne hygiène",
    ]},
    { text: "Lors des soins, le professionnel observe particulièrement les talons, le sacrum, les hanches, les coudes et toute zone à risque." },
  ]},
  { type: 'richtext', title: "Les stades de l'escarre", blocks: [
    { heading: "Stade 1 — Érythème", text: "Rougeur persistante qui ne blanchit pas à la pression du doigt. La peau est intacte mais la zone est chaude, indurée ou douloureuse. C'est le stade réversible si la prise en charge est immédiate." },
    { heading: "Stade 2 — Désépidermisation", text: "Perte partielle de l'épaisseur de la peau. Apparition d'une phlyctène (ampoule) ou d'une abrasion superficielle. Le derme est touché mais la lésion reste superficielle." },
    { heading: "Stade 3 — Nécrose", text: "Perte totale de l'épaisseur de la peau. Le tissu sous-cutané est visible. La plaie peut être profonde avec présence de nécrose (tissu noir) ou de fibrine (tissu jaunâtre)." },
    { heading: "Stade 4 — Atteinte profonde", text: "Perte tissulaire complète avec exposition des muscles, des tendons ou des os. C'est le stade le plus grave, nécessitant une prise en charge médicale et chirurgicale." },
  ]},
  { type: 'richtext', title: "Rôle de l'ASSP dans la prévention", blocks: [
    { list: [
      "Changer la position de la personne toutes les 2 à 3 heures",
      "Utiliser des supports adaptés (matelas à air, coussins de décharge)",
      "Observer la peau lors de chaque toilette et signaler toute rougeur",
      "Maintenir la peau propre et sèche",
      "Favoriser une alimentation équilibrée et une bonne hydratation",
    ]},
  ]},

  // --- 2.2 Prévention des chutes ---
  { type: 'subsection', title: "2.2 Prévention des chutes" },
  { type: 'richtext', title: "Prévention des chutes", blocks: [
    { text: "Les chutes sont fréquentes, notamment chez les personnes âgées ou fragiles. Le professionnel veille à :", list: [
      "Sécuriser l'environnement (sol dégagé, éclairage adapté, barres d'appui)",
      "Vérifier les chaussures (fermées, antidérapantes)",
      "Éliminer les obstacles (tapis, fils, objets au sol)",
      "Adapter l'aide aux déplacements",
      "Installer correctement la personne",
    ]},
    { text: "Chaque mobilisation doit être pensée en fonction des capacités réelles de la personne." },
  ]},

  // --- 2.3 Prévention des infections ---
  { type: 'subsection', title: "2.3 Prévention des infections" },
  { type: 'richtext', title: "Prévention des infections", blocks: [
    { text: "La prévention des infections repose sur :", list: [
      "L'hygiène des mains",
      "Le respect des précautions standard",
      "La bonne utilisation du matériel",
      "Le nettoyage de l'environnement",
      "La gestion correcte du linge et des déchets",
    ]},
    { text: "Le professionnel doit également veiller à ne pas contaminer une zone propre avec du matériel souillé." },
  ]},

  // --- 2.4 Sécurité lors des soins ---
  { type: 'subsection', title: "2.4 Sécurité lors des soins" },
  { type: 'richtext', title: "Sécurité lors des soins", blocks: [
    { text: "La sécurité lors des soins suppose :", list: [
      "Une préparation rigoureuse",
      "Une installation correcte",
      "Une température adaptée de l'eau",
      "Une posture ergonomique du professionnel",
      "Une surveillance continue",
    ]},
    { text: "Il faut prévenir les douleurs, les brûlures, les glissades, les gestes brusques et les situations d'inconfort." },
  ]},

  // =============================================
  // CHAPITRE 3 : Surveillance de l'état de santé
  // =============================================
  { type: 'section', section: "3", title: "Surveillance de l'état de santé" },

  // --- 3.1 Observation clinique ---
  { type: 'subsection', title: "3.1 Observation clinique" },
  { type: 'richtext', title: "Observation clinique", blocks: [
    { text: "Le professionnel joue un rôle majeur dans l'observation. Lors des soins, il repère les modifications :", list: [
      "De l'état général",
      "De la peau",
      "Du comportement",
      "De l'appétit",
      "Du sommeil",
      "De l'élimination",
      "De la mobilité",
    ]},
    { text: "Cette observation permet de détecter précocement une anomalie." },
  ]},

  // --- 3.2 Constantes vitales ---
  { type: 'subsection', title: "3.2 Constantes vitales" },
  { type: 'richtext', title: "Température corporelle", blocks: [
    { text: "Normale : 36,5 °C à 37,5 °C.", list: [
      "Hypothermie : < 35 °C",
      "Fébricule : 37,5 °C à 38 °C",
      "Fièvre (hyperthermie) : > 38 °C",
      "Hyperthermie sévère : > 40 °C",
    ], note: "Mesure : auriculaire, buccale, axillaire, rectale ou frontale." },
  ]},
  { type: 'richtext', title: "Pouls (fréquence cardiaque)", blocks: [
    { text: "Normale adulte : 60 à 80 battements par minute (bpm).", list: [
      "Bradycardie : < 60 bpm",
      "Tachycardie : > 100 bpm",
      "Pouls filant : faible, rapide et difficile à percevoir",
    ], note: "Mesure : artère radiale (poignet), carotide (cou) ou temporal. On observe aussi le rythme (régulier/irrégulier) et l'amplitude." },
  ]},
  { type: 'richtext', title: "Fréquence respiratoire (FR)", blocks: [
    { text: "Normale adulte : 12 à 20 cycles par minute.", list: [
      "Bradypnée : FR < 12/min (respiration anormalement lente)",
      "Tachypnée (ou polypnée) : FR > 20/min (respiration rapide et superficielle)",
      "Dyspnée : difficulté à respirer (sensation d'essoufflement)",
      "Apnée : arrêt temporaire de la respiration",
      "Orthopnée : difficulté à respirer en position allongée",
    ], note: "La FR se mesure discrètement en comptant les mouvements thoraciques sur 1 minute, sans que la personne s'en aperçoive (la respiration se modifie si elle est consciente de l'observation)." },
  ]},
  { type: 'richtext', title: "Tension artérielle (TA)", blocks: [
    { text: "Normale adulte : environ 120/80 mmHg.", list: [
      "Hypertension : TA ≥ 140/90 mmHg",
      "Hypotension : TA systolique < 90 mmHg",
      "TA systolique : pression lors de la contraction du cœur",
      "TA diastolique : pression lors du relâchement du cœur",
    ]},
  ]},
  { type: 'richtext', title: "Saturation en oxygène (SpO2)", blocks: [
    { text: "Normale : 95 % à 100 %.", list: [
      "Désaturation : SpO2 < 95 %",
      "Hypoxie : SpO2 < 90 % (urgence)",
      "Cyanose : coloration bleutée de la peau et des lèvres (signe de manque d'oxygène)",
    ], note: "Mesure : oxymètre de pouls (capteur au doigt)." },
  ]},

  // --- 3.3 Évaluation de la douleur ---
  { type: 'subsection', title: "3.3 Évaluation de la douleur" },
  { type: 'richtext', title: "Évaluation de la douleur", blocks: [
    { text: "La douleur doit être recherchée systématiquement. Elle peut être exprimée verbalement, mais aussi se manifester par des grimaces, une agitation, un repli ou un refus de participation." },
    { text: "Chez certaines personnes, des échelles adaptées sont nécessaires. Toute douleur inhabituelle doit être signalée." },
  ]},
  { type: 'richtext', title: "Échelle Numérique (EN)", blocks: [
    { text: "Le patient évalue sa douleur sur une échelle de 0 à 10.", list: [
      "0 = aucune douleur",
      "1 à 3 = douleur légère",
      "4 à 6 = douleur modérée",
      "7 à 10 = douleur intense à insupportable",
    ], note: "Utilisable chez les personnes communicantes et capables de donner une note." },
  ]},
  { type: 'richtext', title: "Échelle Visuelle Analogique (EVA)", blocks: [
    { text: "Une réglette avec un curseur que le patient positionne entre « pas de douleur » et « douleur maximale imaginable »." },
    { text: "Le professionnel lit la valeur au verso (de 0 à 100 mm). C'est l'outil de référence en milieu hospitalier." },
  ]},
  { type: 'richtext', title: "Algoplus", blocks: [
    { text: "Échelle d'hétéro-évaluation rapide pour les personnes âgées non communicantes. Le professionnel observe 5 items :", list: [
      "Visage (froncement, crispation, grimaces)",
      "Regard (inattentif, fixe, pleurs)",
      "Plaintes (gémissements, cris)",
      "Corps (retrait, protection d'une zone, agitation)",
      "Comportement (agressivité, opposition aux soins)",
    ], note: "Score de 0 à 5. Un score ≥ 2 indique la présence d'une douleur et nécessite une prise en charge." },
  ]},
  { type: 'richtext', title: "ECPA (Échelle Comportementale)", blocks: [
    { text: "Échelle Comportementale de la Douleur chez la Personne Âgée." },
    { text: "Évalue la douleur au repos et pendant les soins. 8 items cotés de 0 à 4. Elle est plus complète qu'Algoplus et adaptée au suivi dans le temps." },
  ]},
  { type: 'richtext', title: "Doloplus-2", blocks: [
    { text: "Échelle d'hétéro-évaluation comportementale de la douleur chronique chez la personne âgée. 10 items répartis en 3 dimensions :", list: [
      "Retentissement somatique (plaintes, positions antalgiques, sommeil)",
      "Retentissement psychomoteur (toilette, mobilité)",
      "Retentissement psychosocial (communication, vie sociale)",
    ], note: "Score de 0 à 30. Un score ≥ 5 évoque la présence d'une douleur." },
  ]},
  { type: 'richtext', title: "EDIN (Douleur du Nouveau-né)", blocks: [
    { text: "Échelle de Douleur et d'Inconfort du Nouveau-né. Utilisée en néonatalogie et pédiatrie." },
    { text: "Observe le visage, le corps, le sommeil et la relation. Adaptée aux nourrissons et jeunes enfants non communicants." },
  ]},

  // --- 3.4 Transmission des informations ---
  { type: 'subsection', title: "3.4 Transmission des informations" },
  { type: 'richtext', title: "Transmission des informations", blocks: [
    { text: "Les transmissions assurent la continuité des soins. Elles doivent être :", list: [
      "Précises",
      "Objectives",
      "Datées",
      "Utiles",
    ]},
    { text: "Le professionnel transmet les observations importantes, les anomalies repérées, la participation de la personne, ses difficultés et l'évolution de son état." },
    { text: "Les transmissions peuvent être écrites ou orales. Elles sont un outil essentiel de communication au sein de l'équipe pluridisciplinaire." },
  ]},
  { type: 'richtext', title: "Transmissions narratives", blocks: [
    { text: "Les transmissions narratives consistent à rédiger un compte-rendu chronologique et descriptif de la situation de la personne." },
    { text: "Le professionnel relate les événements, les observations et les soins réalisés sous forme de récit continu. Ce format permet de donner une vision globale de la prise en charge." },
    { note: "Exemple : « Ce matin, Mme D. a refusé la toilette. Elle semblait fatiguée et douloureuse. J'ai proposé de reporter le soin. Elle a accepté une toilette partielle au lavabo en fin de matinée. »" },
  ]},
  { type: 'richtext', title: "Transmissions ciblées", blocks: [
    { text: "Les transmissions ciblées sont une méthode structurée qui organise l'information autour d'un problème ou d'un événement précis. Elles utilisent le format DAR :", list: [
      "D — Données : ce que l'on observe ou ce que la personne exprime",
      "A — Actions : ce que le professionnel a mis en place",
      "R — Résultats : l'effet des actions entreprises",
    ]},
    { note: "Exemple : Cible : Douleur. D : « Le patient se plaint d'une douleur au genou droit, évaluée à 6/10 sur l'EN. » A : « Repositionnement, application de froid, signalement à l'IDE. » R : « Douleur réduite à 3/10 après 30 minutes. »" },
  ]},

  // =============================================
  // CHAPITRE 4 : Hygiène de l'environnement
  // =============================================
  { type: 'section', section: "4", title: "Hygiène de l'environnement" },

  // --- 4.1 Bionettoyage ---
  { type: 'subsection', title: "4.1 Bionettoyage" },
  { type: 'richtext', title: "Qu'est-ce que le bionettoyage ?", blocks: [
    { text: "Le bionettoyage est un procédé qui associe nettoyage et désinfection. Il permet d'éliminer les salissures visibles et de réduire la présence de micro-organismes sur les surfaces et le matériel." },
    { text: "Le respect des protocoles, des produits et du matériel est essentiel pour garantir la sécurité des personnes et l'efficacité du soin." },
  ]},
  { type: 'richtext', title: "Le détergent", blocks: [
    { text: "Un détergent est un produit de nettoyage qui permet d'éliminer les salissures (graisses, poussières, résidus organiques). Il ne tue pas les micro-organismes mais les décolle des surfaces." },
    { text: "Il est utilisé en première étape du bionettoyage pour préparer la surface avant la désinfection." },
    { note: "Exemple : le savon, les produits ménagers classiques. Le détergent agit par action mécanique et chimique." },
  ]},
  { type: 'richtext', title: "Le désinfectant", blocks: [
    { text: "Un désinfectant est un produit chimique qui détruit ou inactive les micro-organismes présents sur les surfaces. Il agit après le nettoyage, sur une surface propre." },
    { text: "Certains produits combinent les deux actions (détergent-désinfectant) et permettent un bionettoyage en une seule étape." },
    { note: "Attention : un désinfectant appliqué sur une surface sale perd son efficacité. Le nettoyage préalable est indispensable." },
  ]},
  { type: 'richtext', title: "Les micro-organismes", blocks: [
    { text: "Les micro-organismes sont des êtres vivants invisibles à l'œil nu. Ils sont présents partout : sur la peau, dans l'air, sur les surfaces, dans l'eau.", list: [
      "Bactéries : organismes unicellulaires, certaines sont pathogènes (staphylocoque, E. coli)",
      "Virus : agents infectieux qui se multiplient dans les cellules (grippe, COVID, gastro-entérite)",
      "Champignons : responsables de mycoses (candidose, pied d'athlète)",
      "Parasites : organismes vivant aux dépens d'un hôte (poux, gale)",
    ]},
    { note: "La lutte contre les micro-organismes repose sur l'hygiène des mains, le bionettoyage, le respect des précautions standard et la gestion correcte du matériel." },
  ]},
  { type: 'richtext', title: "Protocole de bionettoyage", blocks: [
    { text: "Le bionettoyage suit un ordre logique :", list: [
      "1. Nettoyer avec un détergent pour éliminer les salissures",
      "2. Rincer pour retirer le détergent",
      "3. Désinfecter avec un produit adapté",
      "4. Laisser sécher (le séchage à l'air est recommandé)",
    ]},
    { text: "En cas d'utilisation d'un produit détergent-désinfectant, les étapes de nettoyage et de désinfection sont combinées." },
  ]},

  // --- 4.2 Gestion des déchets ---
  { type: 'subsection', title: "4.2 Gestion des déchets" },
  { type: 'richtext', title: "Principes du tri des déchets", blocks: [
    { text: "Les déchets doivent être triés selon leur nature. Les déchets courants (ordures ménagères) ne sont pas éliminés de la même manière que les déchets à risques infectieux." },
    { text: "Le respect des circuits et des procédures protège à la fois la personne accompagnée, le professionnel et l'environnement." },
  ]},
  { type: 'richtext', title: "Les DASRI", blocks: [
    { text: "Les DASRI (Déchets d'Activités de Soins à Risques Infectieux) regroupent tous les déchets produits lors d'activités de soins pouvant présenter un risque infectieux, chimique ou traumatique.", list: [
      "Matériel piquant ou coupant (aiguilles, lames, lancettes) → collecteur jaune rigide",
      "Pansements souillés, compresses, gants contaminés → sac jaune",
      "Matériel à usage unique ayant été en contact avec du sang ou des liquides biologiques",
    ]},
    { note: "Les DASRI ne doivent JAMAIS être mélangés aux ordures ménagères. Ils sont collectés dans des conteneurs spécifiques (jaunes) et éliminés par des filières spécialisées (incinération)." },
  ]},
  { type: 'richtext', title: "Circuits et procédures", blocks: [
    { text: "Le professionnel veille à :", list: [
      "Ne jamais recapuchonner une aiguille",
      "Utiliser les conteneurs adaptés à chaque type de déchet",
      "Ne pas dépasser le niveau de remplissage maximal des conteneurs",
      "Fermer les conteneurs avant leur transport",
      "Respecter le circuit d'élimination (stockage, collecte, traçabilité)",
    ]},
  ]},

  // --- 4.3 Organisation de l'espace ---
  { type: 'subsection', title: "4.3 Organisation de l'espace" },
  { type: 'richtext', title: "Organisation de l'espace", blocks: [
    { text: "L'organisation de l'espace facilite les soins et réduit les risques. Elle repose sur plusieurs principes :", list: [
      "Le matériel doit être accessible et rangé de manière logique",
      "L'environnement doit être propre, dégagé et sécurisé",
      "Les passages doivent être libres pour faciliter les déplacements",
      "L'éclairage doit être adapté aux activités et à la personne",
    ]},
    { text: "Cette organisation participe à la qualité du soin, à la prévention des accidents et au confort de la personne accompagnée." },
  ]},

  // =============================================
  // CHAPITRE 5 : Alimentation et hydratation
  // =============================================
  { type: 'section', section: "5", title: "Alimentation et hydratation" },

  // --- 5.1 Besoins nutritionnels ---
  { type: 'subsection', title: "5.1 Besoins nutritionnels" },
  { type: 'richtext', title: "Les besoins physiologiques essentiels", blocks: [
    { text: "L'alimentation répond à des besoins physiologiques essentiels :", list: [
      "Besoins énergétiques : fournir l'énergie nécessaire au fonctionnement de l'organisme (activité musculaire, maintien de la température corporelle)",
      "Besoins plastiques : permettre la croissance, le renouvellement et la réparation des cellules et des tissus",
      "Besoins fonctionnels : assurer le bon fonctionnement des organes grâce aux vitamines, minéraux et oligo-éléments",
      "Besoins de protection : renforcer le système immunitaire et prévenir les infections",
      "Besoins hydriques : maintenir l'équilibre en eau de l'organisme (environ 1,5 L d'eau par jour chez l'adulte)",
    ]},
  ]},
  { type: 'richtext', title: "Facteurs influençant les besoins", blocks: [
    { text: "L'alimentation contribue au maintien de la santé, à l'énergie, à la prévention de la dénutrition et au bien-être. Les besoins nutritionnels varient selon :", list: [
      "L'âge (nourrisson, enfant, adulte, personne âgée)",
      "L'état de santé (maladie, convalescence, post-opératoire)",
      "La pathologie (diabète, insuffisance rénale, troubles de la déglutition)",
      "L'activité physique (sédentarité, mobilité réduite, activité intense)",
      "Le degré de dépendance (autonome, aide partielle, aide totale)",
    ]},
  ]},

  // --- 5.2 Aide à la prise des repas ---
  { type: 'subsection', title: "5.2 Aide à la prise des repas" },
  { type: 'richtext', title: "Un acte technique, relationnel et préventif", blocks: [
    { text: "L'aide au repas est un acte à la fois technique, relationnel et préventif. Le repas est un moment physiologique, social et de plaisir. Le professionnel doit favoriser un climat agréable et respecter les habitudes de la personne." },
    { text: "Avant le repas, le professionnel vérifie :", list: [
      "Le régime alimentaire prescrit",
      "L'adaptation éventuelle des textures",
      "L'hygiène des mains (personne et professionnel)",
      "L'installation correcte de la personne",
    ]},
  ]},
  { type: 'richtext', title: "Installation de la personne", blocks: [
    { text: "La personne doit être installée en position assise, idéalement à 90°, afin de limiter le risque de fausse route." },
    { text: "Le buste est droit, la tête légèrement inclinée vers l'avant. Cette position favorise une déglutition sécurisée en dirigeant le bol alimentaire vers l'œsophage et non vers les voies respiratoires." },
  ]},
  { type: 'richtext', title: "Fausse route partielle", blocks: [
    { text: "La fausse route partielle survient lorsqu'un aliment ou un liquide passe partiellement dans les voies respiratoires." },
    { text: "Signes :", list: [
      "Toux réflexe (mécanisme de défense naturel)",
      "Voix enrouée ou modifiée après la déglutition",
      "Sensation de gêne ou de « quelque chose qui passe mal »",
      "Larmoiement",
    ]},
    { note: "La toux est un signe positif : elle montre que l'organisme tente d'expulser le corps étranger. Il ne faut pas empêcher la personne de tousser. Le professionnel arrête le repas, rassure et surveille." },
  ]},
  { type: 'richtext', title: "Fausse route totale", blocks: [
    { text: "La fausse route totale (ou obstruction complète) est une urgence vitale. Les voies aériennes sont totalement obstruées par un aliment." },
    { text: "Signes :", list: [
      "Impossibilité de parler, de tousser ou de respirer",
      "Agitation intense, panique",
      "Cyanose (coloration bleutée du visage et des lèvres)",
      "Perte de connaissance si l'obstruction persiste",
    ]},
    { note: "Conduite à tenir : pratiquer les claques dans le dos (5 claques entre les omoplates) puis les compressions abdominales (manœuvre de Heimlich) si les claques sont inefficaces. Alerter immédiatement les secours." },
  ]},
  { type: 'richtext', title: "Pendant le repas", blocks: [
    { text: "Le professionnel présente le plateau, vérifie la température des aliments, découpe si nécessaire et encourage l'autonomie." },
    { text: "Si une aide est indispensable :", list: [
      "Donner de petites quantités à chaque bouchée",
      "Respecter le rythme de la personne",
      "Laisser le temps de déglutir avant de proposer la bouchée suivante",
      "Rester attentif à la mastication, à la fatigue et à la toux",
    ]},
  ]},
  { type: 'richtext', title: "Surveillance et transmissions", blocks: [
    { text: "Pendant le repas, le professionnel surveille :", list: [
      "La mastication et la déglutition",
      "Les signes de fausse route (toux, voix mouillée)",
      "La fatigue et le comportement alimentaire",
    ]},
    { text: "Le professionnel note et transmet :", list: [
      "Les quantités consommées",
      "Les difficultés rencontrées",
      "Tout refus alimentaire",
    ]},
  ]},

  // --- 5.3 Adaptation des textures ---
  { type: 'subsection', title: "5.3 Adaptation des textures" },
  { type: 'richtext', title: "Adaptation des textures alimentaires", blocks: [
    { text: "Certaines personnes nécessitent une adaptation des textures en raison de troubles de la mastication ou de la déglutition. L'adaptation des textures relève d'une prescription médicale, établie par le médecin ou sur recommandation de l'orthophoniste." },
    { text: "Les différentes textures alimentaires :", list: [
      "Texture normale : alimentation standard, sans modification",
      "Texture hachée : aliments coupés en petits morceaux pour faciliter la mastication",
      "Texture mixée : aliments réduits en purée lisse, adaptée aux troubles sévères de la déglutition",
      "Texture enrichie : ajout de protéines, crème, beurre ou poudre de lait pour augmenter l'apport calorique",
    ]},
    { note: "Le professionnel ne modifie jamais une texture de sa propre initiative. Toute adaptation doit être prescrite et inscrite dans le projet de soin de la personne." },
  ]},
  { type: 'richtext', title: "Adaptation des boissons", blocks: [
    { text: "Les liquides peuvent être difficiles à déglutir car ils s'écoulent rapidement et augmentent le risque de fausse route. Plusieurs adaptations sont possibles :", list: [
      "Eau épaissie : ajout de poudre épaississante pour ralentir l'écoulement du liquide",
      "Eau gélifiée : eau sous forme de gel, plus facile à contrôler en bouche",
      "Eau gazeuse : les bulles stimulent la sensibilité de la bouche et de la gorge, ce qui peut faciliter le réflexe de déglutition",
    ]},
    { note: "Le choix de l'adaptation dépend de l'évaluation des troubles de la déglutition. Il est prescrit par le médecin ou l'orthophoniste." },
  ]},

  // --- 5.4 Prévention de la dénutrition ---
  { type: 'subsection', title: "5.4 Prévention de la dénutrition" },
  { type: 'richtext', title: "Signes de dénutrition", blocks: [
    { text: "La dénutrition est un état pathologique lié à un déséquilibre entre les apports et les besoins de l'organisme. Elle peut se traduire par :", list: [
      "Une perte de poids involontaire et progressive",
      "Une fatigue importante et persistante",
      "Une fonte musculaire (amaigrissement visible)",
      "Une baisse des apports alimentaires",
      "Un désintérêt pour les repas ou un refus alimentaire",
    ]},
    { text: "Le professionnel doit être attentif aux quantités consommées, à l'appétit et au comportement alimentaire. Toute alerte doit être transmise rapidement." },
  ]},
  { type: 'richtext', title: "La pesée : outil de surveillance", blocks: [
    { text: "La pesée est un outil essentiel de surveillance de l'état nutritionnel.", list: [
      "Surveillance classique : pesée une fois par mois, dans les mêmes conditions (même heure, même tenue, même balance)",
      "Surveillance accrue : pesée une fois par semaine en cas de risque de dénutrition identifié, de perte de poids récente ou de pathologie évolutive",
    ]},
    { note: "Une perte de poids de 5 % en 1 mois ou de 10 % en 6 mois est un signe d'alerte de dénutrition. Le résultat doit être noté et transmis à l'équipe soignante." },
  ]},

  // --- 5.5 Hydratation ---
  { type: 'subsection', title: "5.5 Hydratation" },
  { type: 'richtext', title: "Importance de l'hydratation", blocks: [
    { text: "L'hydratation est essentielle au bon fonctionnement de l'organisme. Les besoins sont d'environ 1,5 litre d'eau par jour chez l'adulte." },
    { text: "Le risque de déshydratation est accru chez :", list: [
      "La personne âgée (sensation de soif diminuée)",
      "L'enfant et le nourrisson (réserves en eau limitées)",
      "En période de chaleur ou de fièvre",
      "En cas de diarrhées, vomissements ou maladie",
    ]},
    { text: "Le professionnel doit proposer régulièrement à boire, adapter les contenants (verre à bec, paille) et les textures si besoin (eau épaissie, eau gélifiée)." },
  ]},
  { type: 'richtext', title: "Signes de déshydratation", blocks: [
    { text: "La déshydratation se manifeste par plusieurs signes que le professionnel doit savoir repérer :", list: [
      "Sécheresse de la bouche et des lèvres",
      "Diminution de la quantité d'urines (urines foncées et concentrées)",
      "Fatigue, somnolence, confusion",
      "Perte de poids rapide",
      "Yeux cernés, enfoncés",
      "Pli cutané persistant",
      "Hypotension et accélération du pouls",
    ]},
  ]},
  { type: 'richtext', title: "Tests et signes spécifiques", blocks: [
    { heading: "Le test du pli cutané", text: "Le professionnel pince délicatement la peau du dos de la main ou de l'avant-bras entre le pouce et l'index, puis relâche. Chez une personne bien hydratée, la peau reprend immédiatement sa forme initiale. En cas de déshydratation, le pli cutané persiste plusieurs secondes avant de s'effacer." },
    { note: "Ce test est moins fiable chez la personne âgée (perte d'élasticité naturelle de la peau). Il doit être associé à d'autres signes cliniques." },
  ]},
  { type: 'richtext', title: "La fontanelle chez le nourrisson", blocks: [
    { text: "Chez le nourrisson, la fontanelle (zone membraneuse située au sommet du crâne, entre les os non encore soudés) constitue un indicateur important de l'état d'hydratation." },
    { text: "Une fontanelle enfoncée (déprimée, creusée) est un signe d'alerte de déshydratation. Elle traduit une perte de liquide importante et nécessite une prise en charge rapide." },
    { note: "D'autres signes de déshydratation chez le nourrisson : absence de larmes lors des pleurs, couches sèches (pas d'urines), irritabilité ou au contraire somnolence excessive." },
  ]},

  // --- Attendu VAE Chapitre 5 ---
  { type: 'richtext', title: "Attendu VAE", blocks: [
    { note: "« J'accompagne la prise alimentaire en assurant la sécurité, en respectant les habitudes et en surveillant la déglutition afin de prévenir les fausses routes. Je surveille l'hydratation et l'état nutritionnel en transmettant toute anomalie à l'équipe. »" },
  ]},

  // =============================================
  // CHAPITRE 6 : Accompagnement à l'élimination
  // =============================================
  { type: 'section', section: "6", title: "Accompagnement à l'élimination" },

  // --- 6.1 Fonction d'élimination ---
  { type: 'subsection', title: "6.1 Fonction d'élimination" },
  { type: 'richtext', title: "L'élimination, un besoin fondamental", blocks: [
    { text: "L'élimination est un besoin fondamental de l'être humain. Elle participe à l'équilibre physiologique et au confort de la personne." },
    { text: "Les troubles de l'élimination doivent être repérés et transmis rapidement à l'équipe soignante car ils peuvent révéler un problème de santé sous-jacent." },
  ]},
  { type: 'richtext', title: "La constipation", blocks: [
    { text: "La constipation est un ralentissement du transit intestinal se traduisant par des selles dures, rares (moins de 3 selles par semaine) et difficiles à évacuer." },
    { text: "Causes fréquentes :", list: [
      "Manque d'hydratation",
      "Alimentation pauvre en fibres",
      "Sédentarité ou immobilité prolongée",
      "Certains médicaments (opiacés, antidépresseurs)",
      "Stress ou modification de l'environnement",
    ]},
    { note: "La constipation est fréquente chez la personne âgée et la personne alitée. Le professionnel surveille la fréquence des selles et transmet toute anomalie." },
  ]},
  { type: 'richtext', title: "La diarrhée", blocks: [
    { text: "La diarrhée se caractérise par des selles liquides, fréquentes (plus de 3 par jour) et abondantes." },
    { text: "Causes fréquentes :", list: [
      "Infection virale ou bactérienne (gastro-entérite)",
      "Intolérance alimentaire",
      "Effets secondaires de certains médicaments (antibiotiques)",
      "Stress ou anxiété",
    ]},
    { note: "La diarrhée expose à un risque important de déshydratation, en particulier chez la personne âgée et le nourrisson. Une surveillance accrue de l'hydratation est indispensable." },
  ]},
  { type: 'richtext', title: "L'incontinence", blocks: [
    { text: "L'incontinence est la perte involontaire d'urines ou de selles. Elle peut être :", list: [
      "Incontinence urinaire d'effort : fuite lors d'un effort physique (toux, rire, éternuement)",
      "Incontinence urinaire par urgence : besoin soudain et irrépressible d'uriner",
      "Incontinence fécale : perte involontaire de selles, souvent liée à une pathologie neurologique ou à un affaiblissement du sphincter",
      "Incontinence mixte : association de plusieurs formes",
    ]},
    { note: "L'incontinence a un retentissement important sur l'estime de soi et la vie sociale. Le professionnel adopte une attitude bienveillante, sans jugement ni infantilisation." },
  ]},
  { type: 'richtext', title: "La rétention urinaire", blocks: [
    { text: "La rétention urinaire est l'impossibilité d'évacuer les urines malgré le besoin. La vessie se remplit mais ne se vide pas ou se vide de manière incomplète." },
    { text: "Signes :", list: [
      "Sensation de vessie pleine et douloureuse",
      "Globe vésical (ventre dur et distendu au niveau du bas-ventre)",
      "Agitation ou inconfort inexpliqué",
      "Absence d'urines depuis plusieurs heures",
    ]},
    { note: "La rétention urinaire est une urgence. Le professionnel doit alerter immédiatement l'infirmier ou le médecin." },
  ]},

  // --- 6.2 Accompagnement aux toilettes ---
  { type: 'subsection', title: "6.2 Accompagnement aux toilettes" },
  { type: 'richtext', title: "Accompagnement aux toilettes", blocks: [
    { text: "Lorsque cela est possible, le professionnel privilégie l'accompagnement aux toilettes plutôt que le recours systématique aux protections. Cette démarche favorise l'autonomie et préserve la dignité de la personne." },
    { text: "Le professionnel :", list: [
      "Aide la personne à se déplacer en toute sécurité",
      "L'aide à s'installer confortablement sur les toilettes",
      "L'aide à se relever en respectant l'ergonomie",
      "Reste disponible à proximité tout en respectant l'intimité",
      "Favorise l'autonomie en encourageant la personne à faire seule ce qu'elle peut",
    ]},
    { note: "L'accompagnement aux toilettes contribue à maintenir la continence et l'estime de soi. Le recours aux protections ne doit pas être systématique mais adapté à chaque situation." },
  ]},

  // --- 6.3 Change et soins liés à l'élimination ---
  { type: 'subsection', title: "6.3 Change et soins liés à l'élimination" },
  { type: 'richtext', title: "Principes du change", blocks: [
    { text: "Le change est un soin d'hygiène intime qui doit être réalisé avec respect, discrétion et rigueur. Le professionnel doit :", list: [
      "Informer la personne du soin et obtenir son accord",
      "Réaliser une hygiène des mains avant et après le soin",
      "Mettre la présence (signal lumineux) à l'extérieur de la chambre",
      "Fermer la porte pour préserver l'intimité",
      "Installer la personne confortablement en préservant sa pudeur (utilisation d'un drap ou d'une serviette)",
    ]},
  ]},
  { type: 'richtext', title: "Préparation du matériel", blocks: [
    { text: "Le matériel est préparé en amont pour éviter les interruptions :", list: [
      "Protection propre adaptée à la morphologie",
      "Gants de toilette ou lingettes",
      "Savon doux ou solution lavante",
      "Serviette",
      "Sac à déchets",
      "Crème protectrice si besoin",
      "Gants à usage unique",
    ]},
  ]},
  { type: 'richtext', title: "Réalisation du nettoyage", blocks: [
    { text: "La protection souillée est retirée avec précaution. Le nettoyage se fait du plus propre au plus sale." },
    { note: "Chez la femme, le geste s'effectue d'avant en arrière afin de prévenir les infections urinaires. Chez l'homme, une attention particulière est portée aux plis cutanés et au gland." },
  ]},
  { type: 'richtext', title: "Séchage et mise en place de la protection", blocks: [
    { text: "Après le nettoyage :", list: [
      "La peau est soigneusement séchée, notamment dans les plis cutanés (risque de macération)",
      "Une crème protectrice peut être appliquée si nécessaire (prescription ou protocole de service)",
      "Une protection propre est mise en place, en veillant au confort et au bon positionnement",
      "La personne est réinstallée confortablement",
    ]},
  ]},
  { type: 'richtext', title: "Observation pendant le change", blocks: [
    { text: "Tout au long du soin, le professionnel observe attentivement :", list: [
      "L'état cutané (rougeurs, irritations, lésions)",
      "Les signes d'escarres (zones d'appui)",
      "Les odeurs inhabituelles",
      "Les signes d'infection (chaleur, gonflement, écoulement)",
      "L'aspect des selles et des urines",
    ]},
  ]},
  { type: 'richtext', title: "Transmissions", blocks: [
    { text: "Les anomalies observées doivent être transmises à l'équipe :", list: [
      "Diarrhée ou constipation",
      "Douleurs exprimées ou signes de douleur",
      "Lésions cutanées ou rougeurs persistantes",
      "Modification de l'aspect des urines ou des selles",
      "Comportement inhabituel de la personne pendant le soin",
    ]},
  ]},

  // --- Attendu VAE Chapitre 6 ---
  { type: 'richtext', title: "Attendu VAE", blocks: [
    { note: "« J'accompagne l'élimination en respectant l'intimité, en assurant l'hygiène et en surveillant l'état cutané afin de prévenir les complications. Je transmets toute anomalie à l'équipe soignante. »" },
  ]},

  // =============================================
  // CHAPITRE 7 : Mobilisation, transferts et déplacements
  // =============================================
  { type: 'section', section: "7", title: "Mobilisation, transferts et déplacements" },

  // --- 7.1 Principes d'ergonomie ---
  { type: 'subsection', title: "7.1 Principes d'ergonomie" },
  { type: 'richtext', title: "Définition de l'ergonomie", blocks: [
    { text: "L'ergonomie est la science qui étudie l'adaptation du travail à l'homme. Dans le domaine du soin, elle vise à organiser les gestes, les postures et l'environnement de travail afin de préserver la santé du professionnel tout en assurant la sécurité et le confort de la personne accompagnée." },
    { text: "L'ergonomie repose sur :", list: [
      "L'analyse de la situation avant chaque mobilisation",
      "Le respect des capacités de la personne (ne pas faire à sa place)",
      "L'utilisation des aides techniques adaptées",
      "L'adoption de gestes sécurisés et de postures correctes",
    ]},
  ]},
  { type: 'richtext', title: "Les TMS — Troubles Musculo-Squelettiques", blocks: [
    { text: "Les TMS (Troubles Musculo-Squelettiques) sont des pathologies qui affectent les muscles, les tendons, les nerfs et les articulations. Ils constituent la première cause de maladie professionnelle dans le secteur du soin." },
    { text: "Exemples de TMS fréquents :", list: [
      "Lombalgie : douleur au niveau du bas du dos (la plus fréquente chez les soignants)",
      "Tendinite : inflammation d'un tendon (épaule, coude, poignet)",
      "Hernie discale : déplacement d'un disque intervertébral, souvent lié à des manutentions répétées",
      "Cervicalgie : douleur au niveau des cervicales",
    ]},
    { note: "La prévention des TMS passe par le respect des principes d'ergonomie, l'utilisation systématique des aides techniques disponibles, le travail en binôme lorsque nécessaire et l'entretien physique régulier." },
  ]},
  { type: 'richtext', title: "Principes de manutention sécurisée", blocks: [
    { text: "Pour protéger son dos et assurer la sécurité de la personne, le professionnel respecte plusieurs règles :", list: [
      "Garder le dos droit et fléchir les genoux (ne jamais se pencher en avant dos rond)",
      "Se rapprocher de la personne pour réduire l'effort (bras de levier)",
      "Utiliser la force des jambes, pas celle du dos",
      "Verrouiller le bassin et contracter les abdominaux pendant l'effort",
      "Accompagner le mouvement sans gestes brusques",
      "Demander de l'aide ou utiliser une aide technique si la situation le nécessite",
    ]},
  ]},

  // --- 7.2 Aide au lever et installation ---
  { type: 'subsection', title: "7.2 Aide au lever et installation" },
  { type: 'richtext', title: "Le lever et l'installation", blocks: [
    { text: "Le lever et l'installation font partie des actes quotidiens essentiels. Le professionnel aide la personne à :", list: [
      "Passer du lit au fauteuil (ou inversement)",
      "S'asseoir au bord du lit avant de se lever (position assise intermédiaire)",
      "S'installer confortablement dans le fauteuil ou au lit",
    ]},
    { text: "Le professionnel explique les gestes à la personne, encourage sa participation active et sécurise chaque mouvement. Le lever doit être progressif pour éviter l'hypotension orthostatique (malaise au passage de la position allongée à debout)." },
  ]},
  { type: 'richtext', title: "Prévention lors de l'installation", blocks: [
    { text: "Une bonne installation prévient :", list: [
      "Les douleurs liées à une mauvaise posture",
      "Les chutes lors des transferts",
      "Les points de pression prolongés (risque d'escarres)",
      "La fatigue excessive de la personne",
    ]},
    { note: "Le professionnel vérifie régulièrement le positionnement de la personne et la réinstalle si nécessaire. Les coussins de positionnement peuvent être utilisés pour maintenir une posture confortable et prévenir les points d'appui." },
  ]},

  // --- 7.3 Transferts avec aides techniques ---
  { type: 'subsection', title: "7.3 Transferts avec aides techniques" },
  { type: 'richtext', title: "Le lève-personne", blocks: [
    { text: "Le lève-personne est utilisé pour les transferts de personnes très dépendantes qui ne peuvent pas participer au mouvement. Avant le geste, le professionnel :", list: [
      "Vérifie le bon état du matériel (sangles, harnais, batterie)",
      "Installe correctement le harnais sous la personne, en vérifiant le positionnement",
      "Explique le déroulement du transfert à la personne pour la rassurer",
    ]},
    { text: "Le transfert est réalisé lentement, dans le respect du confort, avec une attention constante portée à la sécurité, à la communication et au bon positionnement." },
    { note: "Ne jamais laisser une personne suspendue dans le lève-personne sans surveillance. Toujours vérifier que les sangles ne compriment pas et que la personne est rassurée." },
  ]},
  { type: 'richtext', title: "Le verticalisateur", blocks: [
    { text: "Le verticalisateur est utilisé pour les personnes pouvant prendre appui sur leurs jambes et participer partiellement au passage assis-debout." },
    { text: "Le professionnel :", list: [
      "Positionne les pieds de la personne sur le plateau du verticalisateur",
      "Fixe la sangle autour du tronc ou sous les bras",
      "Accompagne la montée progressive en position debout",
      "Sécurise la posture et veille à la stabilité de l'ensemble",
    ]},
    { note: "Le verticalisateur favorise l'autonomie et le maintien des capacités motrices. Il est moins contraignant que le lève-personne et valorise la participation de la personne." },
  ]},

  // --- 7.4 Aide à la marche ---
  { type: 'subsection', title: "7.4 Aide à la marche" },
  { type: 'richtext', title: "Accompagner la marche", blocks: [
    { text: "L'aide à la marche consiste à sécuriser le parcours et à accompagner la personne en adaptant l'aide à :", list: [
      "Son état de fatigue",
      "Sa douleur éventuelle",
      "Son équilibre",
      "Son niveau d'autonomie",
    ]},
    { text: "Le professionnel reste attentif au risque de chute et à l'évolution des capacités. Il se place du côté le plus fragile de la personne et adapte son rythme." },
  ]},
  { type: 'richtext', title: "Le déambulateur", blocks: [
    { text: "Le déambulateur (ou rollator) est une aide technique à la marche composé d'un cadre métallique avec des poignées et souvent des roues. Il assure la stabilité et la sécurité lors des déplacements." },
    { text: "Différents types de déambulateurs :", list: [
      "Déambulateur fixe (sans roues) : soulevé et avancé pas à pas, pour les personnes très instables",
      "Déambulateur à 2 roues : plus facile à manœuvrer, pour une marche plus fluide",
      "Rollator à 4 roues avec freins : équipé d'un siège et d'un panier, pour les déplacements plus longs",
    ]},
    { note: "Le professionnel vérifie la hauteur du déambulateur (les poignées doivent arriver au niveau des poignets, bras le long du corps) et s'assure que les freins fonctionnent correctement." },
  ]},
  { type: 'richtext', title: "Transmissions", blocks: [
    { text: "Les difficultés rencontrées, les progrès ou les incidents éventuels doivent être transmis à l'équipe :", list: [
      "Difficultés à la marche (instabilité, fatigue rapide, douleur)",
      "Chutes ou quasi-chutes",
      "Évolution des capacités (amélioration ou dégradation)",
      "Refus de mobilisation ou appréhension",
    ]},
  ]},

  // --- Attendu VAE Chapitre 7 ---
  { type: 'richtext', title: "Attendu VAE", blocks: [
    { note: "« Je mobilise la personne en respectant les principes d'ergonomie, en utilisant les aides techniques adaptées et en favorisant son autonomie. Je préviens les risques de chute et de TMS par des gestes sécurisés et une communication constante. »" },
  ]},

  // =============================================
  // CHAPITRE 8 : Attendus professionnels et VAE
  // =============================================
  { type: 'section', section: "8", title: "Attendus professionnels et VAE" },

  { type: 'richtext', title: "Ce que le jury attend", blocks: [
    { text: "Dans le cadre de la VAE, le jury n'attend pas une simple liste de gestes. Il veut comprendre comment le candidat réfléchit, observe, adapte et sécurise ses pratiques." },
    { text: "Le candidat doit être capable de :", list: [
      "Expliquer ses gestes et leur finalité",
      "Justifier ses choix professionnels",
      "Identifier les risques liés à chaque situation",
      "Observer la personne et repérer les anomalies",
      "Transmettre les informations utiles à l'équipe",
    ]},
  ]},
  { type: 'richtext', title: "Montrer le sens de ses actions", blocks: [
    { text: "Le candidat doit montrer qu'il comprend le sens de ses actions et qu'il agit dans un objectif précis de bien-être, de prévention et de sécurité." },
    { text: "Dire « je fais une toilette » n'est pas suffisant. Il faut pouvoir expliquer que l'on réalise ce soin :", list: [
      "Dans le respect de l'intimité et de la dignité",
      "En stimulant l'autonomie de la personne",
      "En observant l'état cutané (rougeurs, escarres, lésions)",
      "En prévenant les risques (infections, chutes, douleurs)",
      "En transmettant les observations à l'équipe soignante",
    ]},
    { note: "Le jury évalue la capacité du candidat à donner du sens à sa pratique, à faire le lien entre le geste technique et la prise en charge globale de la personne." },
  ]},

  // ===== CONCLUSION =====
  { type: 'richtext', title: "Conclusion du Bloc 2", blocks: [
    { text: "Les soins d'hygiène, de confort et de sécurité sont au cœur de l'accompagnement. Ils exigent des compétences techniques, mais aussi de l'observation, de la rigueur, de l'adaptation et une véritable réflexion professionnelle." },
    { text: "Le professionnel du Bac ASSP doit être capable de relier chaque geste à sa finalité : préserver la santé, la dignité, le confort, l'autonomie et la sécurité de la personne accompagnée." },
  ]},

  // ===== FÉLICITATIONS =====
  { type: 'congratulations', title: "Félicitations !" },
];

const BACASSPBloc2Approche = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [viewedSlides, setViewedSlides] = useState(new Set([0]));
  const audioRef = useRef(null);
  const API_URL = window.location.origin || '';

  useEffect(() => {
    const saved = getActivityProgress(ACTIVITY_ID);
    if (saved?.viewedSlides) setViewedSlides(new Set(saved.viewedSlides));
  }, []);

  const speakText = async (text) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/tts/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: text.substring(0, 800), voice: 'nova', speed: 1.1 }) });
      if (r.ok) { const d = await r.json(); if (d.audio_url || d.audio_base64) { if (audioRef.current) audioRef.current.pause(); audioRef.current = new Audio(d.audio_url || `data:audio/mp3;base64,${d.audio_base64}`); audioRef.current.play(); setIsPlaying(true); audioRef.current.onended = () => setIsPlaying(false); } }
    } catch (e) { console.log('TTS unavailable'); }
    setIsLoading(false);
  };
  const stopAudio = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; setIsPlaying(false); } };
  const getSlideText = (s) => { let t = s.title || ''; if (s.blocks) s.blocks.forEach(b => { if (b.heading) t += ' ' + b.heading; if (b.text) t += ' ' + b.text; if (b.list) b.list.forEach(li => t += ' ' + li); }); return t; };

  const nextSlide = () => { if (currentSlide < slides.length - 1) { const next = currentSlide + 1; setCurrentSlide(next); setViewedSlides(prev => new Set([...prev, next])); markSlideViewed(ACTIVITY_ID, next, slides.length); } };
  const prevSlide = () => { if (currentSlide > 0) setCurrentSlide(currentSlide - 1); };
  const goToSlide = (i) => { setCurrentSlide(i); setViewedSlides(prev => new Set([...prev, i])); markSlideViewed(ACTIVITY_ID, i, slides.length); };

  const progressPercent = Math.round((viewedSlides.size / slides.length) * 100);

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div className="flex items-center justify-center h-full" style={{ background: theme.bg }}>
            <img src={slide.image} alt="Bloc 2" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
        );
      case 'sommaire':
        return (
          <div className="h-full flex flex-col relative" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(240,253,250,0.78)' }} />
            <div className="relative z-10 h-full flex flex-col">
              <div style={{ background: `linear-gradient(135deg, ${theme.primary} 0%, #065F46 100%)`, padding: '0.6rem 0' }}>
                <h2 className="text-xl font-bold text-center text-white tracking-widest uppercase">Sommaire</h2>
              </div>
              <div className="flex-1 overflow-auto px-4 flex items-center justify-center">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto">
                  {slide.sections.map((s, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-white/90 rounded-lg shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all backdrop-blur-sm" style={{ border: '1px solid #e0e0e0' }} onClick={() => goToSlide(s.target - 1)}>
                      <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 text-white" style={{ background: theme.primary }}>{s.num}</span>
                      <span className="text-gray-700 text-sm leading-tight font-medium">{s.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 'section':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(13,148,136,0.45)' }} />
            <div className="text-center px-10 relative z-10">
              <span className="text-white/80 text-2xl md:text-3xl font-bold mb-3 block tracking-wide">Chapitre {slide.section}</span>
              <h2 className="text-xl md:text-2xl font-semibold text-white drop-shadow-lg">{slide.title}</h2>
            </div>
          </div>
        );
      case 'subsection':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(13,148,136,0.35)' }} />
            <div className="text-center px-10 relative z-10">
              <h2 className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg">{slide.title}</h2>
            </div>
          </div>
        );
      case 'richtext':
        return (
          <div className="h-full overflow-auto flex items-center justify-center p-6" style={{ background: theme.bg }}>
            <div className="max-w-3xl w-full">
              <h2 className="text-lg font-bold mb-4 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              <div className="space-y-3">
                {slide.blocks.map((block, i) => (
                  <div key={i} className="bg-white rounded-lg p-4 shadow-sm" style={{ border: `1px solid ${theme.primary}12` }}>
                    {block.heading && <h4 className="font-semibold mb-2 text-[15px]" style={{ color: theme.primary }}>{block.heading}</h4>}
                    {block.text && <p className="text-gray-700 text-[15px] leading-relaxed mb-2">{block.text}</p>}
                    {block.list && (
                      <div className="space-y-1.5">
                        {block.list.map((li, j) => (
                          <div key={j} className="flex items-start gap-2.5">
                            <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: theme.primary }} />
                            <span className="text-gray-600 text-[15px] leading-relaxed">{li}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {block.note && <p className="text-[14px] italic mt-2.5 p-2.5 rounded-md" style={{ background: `${theme.primary}08`, borderLeft: `3px solid ${theme.primary}`, color: '#475569' }}>{block.note}</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'congratulations':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(13,148,136,0.5)' }} />
            <div className="relative z-10 flex flex-col items-center text-center px-8 max-w-2xl">
              <div className="text-5xl mb-4">&#127942;</div>
              <h2 className="text-3xl font-bold text-white mb-4 drop-shadow-lg">{slide.title}</h2>
              <p className="text-white text-lg mb-3 leading-relaxed drop-shadow">Vous avez terminé la présentation du <strong>Bloc 2</strong> du BAC ASSP.</p>
              <p className="text-white text-base mb-6 leading-relaxed drop-shadow">Vous maîtrisez désormais les fondamentaux des soins d'hygiène, de confort et de sécurité.</p>
              <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.9)' }}>
                <p className="text-base font-semibold mb-1" style={{ color: theme.primary }}>Prêt(e) à tester vos connaissances ?</p>
                <p className="text-sm text-gray-600">Rendez-vous dans le <strong>Quiz - Bloc 2</strong> pour évaluer votre maîtrise.</p>
              </div>
            </div>
          </div>
        );
      default: return null;
    }
  };

  const slide = slides[currentSlide];
  return (
    <div className="rounded-xl overflow-hidden shadow-lg" style={{ background: theme.bg }} data-testid="bac-assp-bloc2-presentation">
      <div style={{ minHeight: '480px', height: '480px' }}>{renderSlide(slide)}</div>
      <div className="w-full h-2 bg-gray-200">
        <div className="h-full rounded-r-full transition-all duration-300" style={{ width: `${progressPercent}%`, background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})` }} />
      </div>
      <div className="flex items-center justify-between p-3 bg-white border-t" style={{ borderColor: theme.primary + '30' }}>
        <button onClick={prevSlide} disabled={currentSlide === 0} className="flex items-center gap-1 px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-40" style={{ borderColor: theme.primary, color: theme.text }} data-testid="bloc2-prev-btn">
          <ChevronLeft size={16} /> Précédent
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => isPlaying ? stopAudio() : speakText(getSlideText(slide))} className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ background: theme.primary }} data-testid="bloc2-audio-btn">
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : isPlaying ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <span className="font-bold text-sm" style={{ color: theme.primary }}>{currentSlide + 1} / {slides.length}</span>
          <span className="text-xs text-gray-400">{progressPercent}%</span>
        </div>
        <button onClick={nextSlide} disabled={currentSlide === slides.length - 1} className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40" style={{ background: theme.primary }} data-testid="bloc2-next-btn">
          Suivant <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default BACASSPBloc2Approche;
